
import VueMultiselect from "vue-multiselect";
import { IFormFieldModel, IOptionModel } from "@/interfaces/iAddEditFormModel";
import {
  defineComponent,
  useAttrs,
  ref,
  computed,
  PropType,
  watch,
  toRefs,
} from "vue";

export default defineComponent({
  name: "VZMulitSelect",
  inheritAttrs: false,
  components: {
    VueMultiselect,
  },
  props: {
    element: { type: Object as PropType<IFormFieldModel>, default: null },
    modelValue: {
      type: Array as PropType<string[]>,
      default: null,
    },
    maxSelectedItems: {
      type: String,
      default: "50",
    },
  },
  setup(props, { emit }) {
    const selectedItems = ref();
    const attrs = useAttrs();
    const { modelValue } = toRefs(props);
    const { element, maxSelectedItems } = toRefs(props);

    const selectAllOption = { label: "Select All", value: "select_all" };
    const removeAllOption = { label: "Remove All", value: "remove_all" };

    const isAllSelected = computed(() => {
      const options = element.value.options ?? [];
      return selectedItems.value?.length === options.length;
    });

    const computedOptions = computed(() => {
      const options = element.value.options ?? [];
      if (options.length === 0) {
        return [];
      }
      return selectedItems.value?.length === options.length
        ? [removeAllOption, ...options]
        : [selectAllOption, ...options];
    });

    const limitSelectedItems = computed(() => {
      let index = 99999;
      let counter = 0;
      for (let i = 0; i < selectedItems.value?.length; i++) {
        const text = selectedItems.value[i].label;
        if (text.length + counter < +maxSelectedItems.value) {
          counter = counter + text.length;
        } else {
          index = i;
          break;
        }
      }
      return index <= 0 ? 1 : index;
    });

    watch(
      selectedItems,
      (newValue) => {
        if (newValue?.find((item) => item.value === "select_all")) {
          selectedItems.value = element.value.options
            ? [...element.value.options]
            : [];
        } else if (newValue?.find((item) => item.value === "remove_all")) {
          selectedItems.value = [];
        } else if (!element.value.multiSelect) {
          emit("update:modelValue", newValue?.value ?? "");
        } else {
          emit(
            "update:modelValue",
            selectedItems.value?.map((item) => item.value) ?? []
          );
        }
      },
      { deep: true }
    );

    watch(
      modelValue,
      (newValue: string[]) => {
        if (!newValue || !newValue.length) {
          selectedItems.value = null;
        } else if (
          newValue &&
          newValue.length !== selectedItems?.value?.length &&
          element?.value?.options
        ) {
          const options = element?.value.options ?? [];
          if (options) {
            const items: IOptionModel[] =
              options?.filter((itemValue: IOptionModel) => {
                if (newValue.includes(`${itemValue.value}`)) {
                  return itemValue;
                }
                return false;
              }) ?? [];
            if (items && items.length > 0) {
              selectedItems.value = [...items];
            }
          }
        }
      },
      { deep: true }
    );

    // Initial setup based on modelValue
    if (props.modelValue && props.modelValue.length > 0) {
      const options = element.value.options ?? [];
      const items: IOptionModel[] = options.filter((item) =>
        props.modelValue.includes(String(item.value))
      );
      selectedItems.value = items.length > 0 ? items : [];
    }

    return {
      computedOptions,
      limitSelectedItems,
      selectedItems,
      attrs,
      isAllSelected,
    };
  },
});
